import { useContext, useEffect, useState } from "react";

import {
  Card,
  CardContent,
  CardMedia,
  Grid,
  Typography,
  Chip,
} from "@mui/material";
import { SubmissionSummary } from "@rubi/api-generated";
import { colors } from "@rubi/ui";

import { formatDateShort } from "@/common/utils";
import { OnlineReportingContext } from "@/components/online-reporting/context";
import { getTitleFromSubmission } from "@/components/online-reporting/utils";
import { reportColourTypes, statusTypes } from "@/types/typesReportV2";
import {
  fetchSubmissionData,
  SubmissionData,
  parseMediaDetails,
} from "@/utils/submissionUtils";

// Status color mapping for different registry states
const statusColorMapping = {
  Active: colors.success.light, // Green for active entries
  Archived: colors.action.disabled, // Blue for archived
  Flagged: colors.error.light, // Red for flagged items
  "Under Review": colors.action.hover, // Yellow for items under review
};

const RegistryCard = ({
  index,
  registryItem,
  onCardClick,
}: {
  index: number;
  registryItem: SubmissionSummary;
  onCardClick: (item: SubmissionSummary) => void;
}) => {
  const [submissionData, setSubmissionData] = useState<SubmissionData>({
    details: null,
    attachments: [],
  });
  const [loading, setLoading] = useState(true);
  const { getSubmissionForID } = useContext(OnlineReportingContext);

  useEffect(() => {
    const loadSubmissionData = async () => {
      try {
        const data = await fetchSubmissionData(
          registryItem.id,
          getSubmissionForID,
        );
        setSubmissionData(data);
      } finally {
        setLoading(false);
      }
    };

    loadSubmissionData();
  }, [registryItem.id, getSubmissionForID]);

  const mediaDetails = parseMediaDetails(submissionData.attachments);

  return (
    <Card
      onClick={() => onCardClick(registryItem)}
      sx={{
        cursor: "pointer",
        "&:hover": { boxShadow: 6 },
      }}
    >
      {mediaDetails.length > 0 && (
        <Grid container spacing={1} sx={{ p: 1 }}>
          {mediaDetails.map((media, idx) => (
            <Grid size={{ xs: mediaDetails.length === 1 ? 12 : 6 }} key={idx}>
              <CardMedia
                component={media.media_type === "video" ? "video" : "img"}
                src={media.url}
                sx={{
                  height: mediaDetails.length === 1 ? 200 : 100,
                  objectFit: "cover",
                }}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <CardContent>
        <Typography variant="h6" gutterBottom>
          {submissionData.details &&
            getTitleFromSubmission(
              submissionData.details,
              registryItem.survey_name ?? "",
            )}
        </Typography>

        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {registryItem.survey_name}
        </Typography>

        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Submitted by: {registryItem.first_name} {registryItem.last_name}
        </Typography>

        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Date: {formatDateShort(registryItem.time_created)}
        </Typography>

        {registryItem.tracking_number && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Tracking #: {registryItem.tracking_number}
          </Typography>
        )}
        <Chip
          sx={{
            mt: 1,
          }}
          style={{
            opacity:
              registryItem.status === "rejected" ||
              registryItem.status === "approved"
                ? 0.6
                : 1,
          }}
          label={
            statusTypes[registryItem.status]
              ? statusTypes[registryItem.status]
              : registryItem.status
          }
          color={
            reportColourTypes[registryItem.status]
              ? reportColourTypes[registryItem.status]
              : "default"
          }
        />
      </CardContent>
    </Card>
  );
};

export default RegistryCard;
